import type { HTMLAttributes } from 'react';

import React, { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  NextSlideButton,
  PrevSlideButton,
  ScrollLeftButton,
  ScrollRightButton,
  SlideButton,
} from './presentation-preview-buttons';
import { useOnResize } from '../../hooks/use-on-resize';
import { CloseIcon } from '../../icons';
import { clamp } from '../../utils/clamp';
import { classNames } from '../../utils/class-names';
import { IconButton } from '../buttons';
import { Tooltip } from '../tooltip';

import styles from './presentation-preview.module.css';

export interface Slide {
  index: number;
  downloadUrl: string;
  thumbnailDownloadUrl: string;
}

export type PresentationPreviewProps = {
  slides?: Slide[];
  onClose?: () => void;
} & HTMLAttributes<HTMLElement>;

const SLIDES_GAP = 10;

/**
 * @param {Slide[]} [slides] The slides to display in the presentation preview
 * @default []
 * @param {Function} [onClose] Callback to invoke when the close button is clicked
 * @default undefined
 */
export function PresentationPreview({ slides = [], onClose, ...props }: PresentationPreviewProps) {
  const { t } = useTranslation();
  const slidesListRef = useRef<HTMLDivElement>(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [arrowsVisible, setArrowsVisible] = useState(false);
  const [leftArrowDisabled, setLeftArrowDisabled] = useState(false);
  const [rightArrowDisabled, setRightArrowDisabled] = useState(false);

  const currentSlidePreview = slides[currentSlideIndex]?.downloadUrl;
  const prevSlideButtonVisibile = currentSlideIndex > 0;
  const nextSlideButtonVisibile = currentSlideIndex < slides.length - 1;

  const getDomElements = () => {
    const wrapper = slidesListRef.current;
    const slides = Array.from(wrapper?.children || []) as HTMLButtonElement[];

    return { wrapper, slides };
  };

  const showPrevSlide = () => {
    setCurrentSlideIndex(clamp(currentSlideIndex - 1, 0, slides.length - 1));
  };

  const showNextSlide = () => {
    setCurrentSlideIndex(clamp(currentSlideIndex + 1, 0, slides.length - 1));
  };

  const scrollLeft = () => {
    const { wrapper } = getDomElements();

    if (!wrapper) return;

    wrapper.scrollLeft -= wrapper.offsetWidth;
  };

  const scrollRight = () => {
    const { wrapper } = getDomElements();

    if (!wrapper) return;

    wrapper.scrollLeft += wrapper.offsetWidth;
  };

  const updateArrowsDisabledState = () => {
    const { wrapper } = getDomElements();

    if (!wrapper) return;

    const scrollLeft = wrapper.scrollLeft;
    const scrollRight = scrollLeft + wrapper.offsetWidth;

    setLeftArrowDisabled(scrollLeft <= SLIDES_GAP);
    setRightArrowDisabled(scrollRight >= wrapper.scrollWidth - SLIDES_GAP);
  };

  const scrollIntoCurrentSlideButton = () => {
    getDomElements().slides[currentSlideIndex]?.scrollIntoView({ inline: 'center' });
  };

  const checkArrowsVisibility = () => {
    const { slides, wrapper } = getDomElements();

    if (!wrapper) return;

    const slidesWidth = (slides[0]?.offsetWidth + SLIDES_GAP) * slides.length;

    setArrowsVisible(slidesWidth > wrapper.offsetWidth);
  };

  useLayoutEffect(checkArrowsVisibility, [checkArrowsVisibility]);
  useLayoutEffect(scrollIntoCurrentSlideButton, [currentSlideIndex]);

  /**
   * This code is not tested automatically because resizing the window
   * is not supported with React testing library.
   */
  useOnResize(slidesListRef, () => {
    updateArrowsDisabledState();
    checkArrowsVisibility();
  });

  return (
    <div
      className={classNames(styles.presentationPreview, props.className)}
      data-testid="presentation-preview"
      {...props}
    >
      <header>
        <span>{t('presentationPreview.header.title')}</span>

        {prevSlideButtonVisibile && <PrevSlideButton onClick={showPrevSlide} />}
        {nextSlideButtonVisibile && <NextSlideButton onClick={showNextSlide} />}

        <Tooltip label={t('presentationPreview.button.close.label')}>
          <IconButton
            data-testid="presentation-preview-close-button"
            rounded
            type="button"
            aria-label={t('presentationPreview.button.close.label')}
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </header>

      <main
        role="tabpanel"
        style={currentSlidePreview ? { backgroundImage: `url(${currentSlidePreview})` } : undefined}
      />

      <footer>
        {arrowsVisible && <ScrollLeftButton onClick={scrollLeft} disabled={leftArrowDisabled} />}

        <div ref={slidesListRef} role="tablist" className={styles.slidesList} onScroll={updateArrowsDisabledState}>
          {slides.map((slide, index) => (
            <SlideButton
              key={index}
              active={index === currentSlideIndex}
              image={slide.thumbnailDownloadUrl}
              label={t('presentationPreview.slide', { number: slide.index })}
              onClick={() => setCurrentSlideIndex(index)}
            />
          ))}
        </div>

        {arrowsVisible && <ScrollRightButton onClick={scrollRight} disabled={rightArrowDisabled} />}
      </footer>
    </div>
  );
}
